import useAuth from "../../hooks/useAuth";
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as orderService from '../../services/orderService';
import * as productService from '../../services/productService';
import styles from './DashboardPage.module.css';
import { resolveCategoryHeroUrl } from '../../utils/categoryHero';
import { ASYNC_REVIEW_NOTICE, APPROVAL_UPDATE_NOTICE } from '../../utils/asyncConsultCopy';

// Card 40755 — the statuses that mean "a doctor has the intake and has not decided yet".
// `getStatusBadge` below stays the single source of the status NAME; this is the async
// EXPLANATION rendered beside it, and its sentences are imported from
// `utils/asyncConsultCopy` so the dashboard, the order list, checkout and the order emails
// cannot drift apart the way they did before that module existed.
const ASYNC_REVIEW_STATUSES = ['pending', 'under_review'];

const getAsyncStatusHint = (status) =>
  ASYNC_REVIEW_STATUSES.includes(status)
    ? `${ASYNC_REVIEW_NOTICE} ${APPROVAL_UPDATE_NOTICE}`
    : null;

const CategoryPickerModal = ({ categories, products, onClose, loadError }) => {
  const navigate = useNavigate();

  const getStartedLink = (category) => {
    const catProducts = products.filter(p => p.category?.slug === category.slug);
    // Category super offers were removed on 2026-09-21 (card 53748): the picker now goes
    // straight to the selected category's first product, with no `?offer=` query string.
    const firstProduct = catProducts[0];
    if (firstProduct) {
      return `/intake/${firstProduct.slug || firstProduct.id}`;
    }
    return '/catalog';
  };

  const handleSelect = (category) => {
    const link = getStartedLink(category);
    onClose();
    navigate(link);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>&times;</button>
        <h2 className={styles.modalTitle}>What type of treatment are you looking for?</h2>
        {loadError ? (
          <p className={styles.modalSubtitle} role="alert">
            Treatment categories could not be loaded. Please close this and try again in a moment.
          </p>
        ) : (
          <p className={styles.modalSubtitle}>Select a category to start your intake</p>
        )}
        <div className={styles.categoryGrid}>
          {categories.map(cat => (
            <button
              key={cat.id}
              className={styles.categoryCard}
              onClick={() => handleSelect(cat)}
            >
              <div
                className={styles.categoryCardImage}
                style={{ backgroundImage: `url(${resolveCategoryHeroUrl(cat)})` }}
              />
              <span className={styles.categoryCardName}>{cat.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const displayName = user?.profile?.firstName || user?.firstName || user?.first_name || user?.name || "there";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [ordersError, setOrdersError] = useState(false);
  const [catalogError, setCatalogError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const response = await orderService.getMyOrders({ limit: 5 });
        setOrders(response.orders || []);
      } catch (error) {
        console.error('Failed to load orders:', error);
        setOrdersError(true);
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, []);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const [catResponse, prodResponse] = await Promise.all([
          productService.getCategories(),
          productService.getProducts()
        ]);
        setCategories(catResponse.categories || []);
        setProducts(prodResponse.products || []);
      } catch (error) {
        console.error('Failed to load catalog:', error);
        setCatalogError(true);
      }
    };
    loadCatalog();
  }, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7' },
      under_review: { label: 'Under Review', color: '#3b82f6', bg: '#dbeafe' },
      approved: { label: 'Approved', color: '#10b981', bg: '#d1fae5' },
      denied: { label: 'Denied', color: '#ef4444', bg: '#fee2e2' },
      processing: { label: 'Processing', color: '#8b5cf6', bg: '#ede9fe' },
      shipped: { label: 'Shipped', color: '#06b6d4', bg: '#cffafe' },
      delivered: { label: 'Delivered', color: '#22c55e', bg: '#dcfce7' },
      cancelled: { label: 'Cancellation Requested', color: '#6b7280', bg: '#f3f4f6' }
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const QuickActionButtons = () => (
    <div className={styles.quickPills}>
      <button
        onClick={() => setShowCategoryPicker(true)}
        className={`${styles.quickPill} ${styles.quickPillPrimary}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.5 20.5L3.5 13.5C2.5 12.5 2.5 10.5 3.5 9.5l7-7c1-1 3-1 4 0l7 7c1 1 1 3 0 4l-7 7c-1 1-3 1-4 0z"/>
          <path d="M8.5 8.5l7 7"/>
        </svg>
        Request Medication
      </button>
      <Link to="/catalog" className={styles.quickPill}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
        Browse Catalog
      </Link>
      <Link to="/orders" className={styles.quickPill}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
        My Orders
      </Link>
      <Link to="/notifications" className={styles.quickPill}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        Notifications
      </Link>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Category Picker Modal */}
      {showCategoryPicker && (
        <CategoryPickerModal
          categories={categories}
          products={products}
          loadError={catalogError}
          onClose={() => setShowCategoryPicker(false)}
        />
      )}

      {/* Welcome Banner */}
      <div className={styles.welcomeBanner}>
        <div className={styles.mobileVideoWrapper}>
          <video
            className={styles.videoBg}
            src="/images/landing-page/cta-video.mp4"
            poster="/images/landing-page/cta-video-poster.webp"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className={styles.blurOverlay}></div>
        </div>

        <div className={styles.welcomeLeft}>
          <div className={styles.glassBox}>
            <span className={styles.onlineTag}><span className={styles.onlineDot}></span> Online</span>
            <h1>Welcome back,<br />{displayName}</h1>
            <p className={styles.welcomeSubtitle}>Your personal health dashboard</p>
            <QuickActionButtons />
            <p className={styles.supportText}>
              Our care team is available 24/7. <a href="mailto:support@ovalcare.com">Message support</a>
            </p>
          </div>
        </div>

        <div className={styles.welcomeRight}>
          <video
            className={styles.welcomeVideo}
            src="/images/landing-page/cta-video.mp4"
            poster="/images/landing-page/cta-video-poster.webp"
            autoPlay
            muted
            loop
            playsInline
          />
        </div>

        <div className={styles.mobileContent}>
          <div className={styles.glassBox}>
            <span className={styles.onlineTag}><span className={styles.onlineDot}></span> Online</span>
            <h1>Welcome back,<br />{displayName}</h1>
            <p className={styles.welcomeSubtitle}>Your personal health dashboard</p>
            <QuickActionButtons />
            <p className={styles.supportText}>
              Our care team is available 24/7. <a href="mailto:support@ovalcare.com">Message support</a>
            </p>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className={styles.recentOrders}>
          <div className={styles.ordersHeader}>
            <h2>Recent Orders</h2>
            <Link to="/orders" className={styles.viewOrdersBtn}>View Orders</Link>
          </div>

          <div className={styles.tableScroll}><div className={styles.ordersTable}>
            <div className={styles.tableHeader}>
              <span>Order</span>
              <span>Item</span>
              <span>Date</span>
              <span>Status</span>
              <span>Total</span>
              <span>Actions</span>
            </div>

            {loading ? (
              <div className={styles.tableLoading}>Loading...</div>
            ) : ordersError ? (
              <div className={styles.tableEmpty} role="alert">
                Your recent orders could not be loaded. Please refresh the page to try again.
              </div>
            ) : orders.length === 0 ? (
              <div className={styles.tableEmpty}>You have no recent orders.</div>
            ) : (
              <div className={styles.tableBody}>
                {orders.map((order) => {
                  const status = getStatusBadge(order.status);
                  return (
                    <div key={order.id} className={styles.tableRow}>
                      <span className={styles.orderNumber}>{order.orderNumber}</span>
                      <span className={styles.orderItem}>
                        {order.items?.[0]?.product?.name || order.items?.[0]?.productName || '-'}
                      </span>
                      <span className={styles.orderDate}>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </span>
                      <span>
                        <span
                          className={styles.statusBadge}
                          style={{ color: status.color, backgroundColor: status.bg }}
                        >
                          {status.label}
                        </span>
                      </span>
                      <span className={styles.orderTotal}>{order.pricing?.formattedTotal || '-'}</span>
                      <span>
                        <Link to="/orders" className={styles.viewBtn}>View</Link>
                      </span>
                      {getAsyncStatusHint(order.status) && (
                        <span className={styles.statusHint}>
                          {getAsyncStatusHint(order.status)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>
    </div>
  );
};

export default DashboardPage;
